/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Nucleus Admin API
 * Headless, multi-tenant commerce backend for jewelry brands.
 *
 * ## Surfaces
 *
 * | Prefix | Caller | Credential |
 * |---|---|---|
 * | `/platform/v1` | Platform operator | Platform token from **Platform: Auth** |
 * | `/admin/v1` | Staff of one organization | Staff token from **Admin: Auth** |
 * | `/storefront/v1` | A storefront | Store API key from **Admin: API keys** |
 * | `/public/v1` | Anyone (the brand application form) | None; rate limited per IP |
 *
 * Every credential is sent as `Authorization: Bearer <value>`. Use the **Authorize** button once and it applies to every request you try here.
 *
 * ## Conventions
 *
 * - **Ids** are UUID v7 strings. **Timestamps** are ISO 8601 in UTC. **Money** is `{ amount, currency }` with `amount` in integer minor units.
 * - **Lists** paginate by cursor: `?limit=20&cursor=...` in, `{ items, nextCursor }` out, `nextCursor` is `null` on the last page.
 * - **Errors** always use one envelope, `{ error: { code, message, requestId, details?, meta? } }`. Switch on `code`, never on `message`.
 * - **Request ids**: every response carries `x-request-id`. Quote it when reporting a problem.
 * - **Idempotency**: endpoints that create money-related state require an `Idempotency-Key` header; the header is listed on those endpoints.
 * - A record that belongs to another tenant answers **404**, never 403, so existence is not leaked.
 * OpenAPI spec version: 0.1.0
 */
import type {
  AdminAuction,
  AdminAuctionQuestion,
  AdminAuctionRegistration,
  AdminAuctionSale,
  DeleteAdminV1StoresStoreIdSalesIdLotsAuctionIdPathParameters,
  GetAdminV1StoresStoreIdAuctions200,
  GetAdminV1StoresStoreIdAuctionsIdBids200,
  GetAdminV1StoresStoreIdAuctionsIdBidsParams,
  GetAdminV1StoresStoreIdAuctionsIdBidsPathParameters,
  GetAdminV1StoresStoreIdAuctionsIdOffers200,
  GetAdminV1StoresStoreIdAuctionsIdOffersPathParameters,
  GetAdminV1StoresStoreIdAuctionsIdPathParameters,
  GetAdminV1StoresStoreIdAuctionsParams,
  GetAdminV1StoresStoreIdAuctionsPathParameters,
  GetAdminV1StoresStoreIdAuctionsQuestions200,
  GetAdminV1StoresStoreIdAuctionsQuestionsParams,
  GetAdminV1StoresStoreIdAuctionsQuestionsPathParameters,
  GetAdminV1StoresStoreIdAuctionsRegistrations200,
  GetAdminV1StoresStoreIdAuctionsRegistrationsParams,
  GetAdminV1StoresStoreIdAuctionsRegistrationsPathParameters,
  GetAdminV1StoresStoreIdSales200,
  GetAdminV1StoresStoreIdSalesIdPathParameters,
  GetAdminV1StoresStoreIdSalesParams,
  GetAdminV1StoresStoreIdSalesPathParameters,
  PatchAdminV1StoresStoreIdAuctionsIdBody,
  PatchAdminV1StoresStoreIdAuctionsIdPathParameters,
  PatchAdminV1StoresStoreIdSalesIdBody,
  PatchAdminV1StoresStoreIdSalesIdLotsOrderBody,
  PatchAdminV1StoresStoreIdSalesIdLotsOrderPathParameters,
  PatchAdminV1StoresStoreIdSalesIdPathParameters,
  PostAdminV1StoresStoreIdAuctionsBody,
  PostAdminV1StoresStoreIdAuctionsIdBidsBidIdRetractBody,
  PostAdminV1StoresStoreIdAuctionsIdBidsBidIdRetractPathParameters,
  PostAdminV1StoresStoreIdAuctionsIdCancelBody,
  PostAdminV1StoresStoreIdAuctionsIdCancelPathParameters,
  PostAdminV1StoresStoreIdAuctionsIdEndPathParameters,
  PostAdminV1StoresStoreIdAuctionsIdOffersOfferIdWithdrawPathParameters,
  PostAdminV1StoresStoreIdAuctionsIdSecondChanceBody,
  PostAdminV1StoresStoreIdAuctionsIdSecondChancePathParameters,
  PostAdminV1StoresStoreIdAuctionsPathParameters,
  PostAdminV1StoresStoreIdAuctionsQuestionsIdAnswerBody,
  PostAdminV1StoresStoreIdAuctionsQuestionsIdAnswerPathParameters,
  PostAdminV1StoresStoreIdAuctionsQuestionsIdHideBody,
  PostAdminV1StoresStoreIdAuctionsQuestionsIdHidePathParameters,
  PostAdminV1StoresStoreIdAuctionsRegistrationsIdBlockBody,
  PostAdminV1StoresStoreIdAuctionsRegistrationsIdBlockPathParameters,
  PostAdminV1StoresStoreIdAuctionsRegistrationsIdUnblockPathParameters,
  PostAdminV1StoresStoreIdSalesBody,
  PostAdminV1StoresStoreIdSalesIdCancelBody,
  PostAdminV1StoresStoreIdSalesIdCancelPathParameters,
  PostAdminV1StoresStoreIdSalesIdLotsBody,
  PostAdminV1StoresStoreIdSalesIdLotsPathParameters,
  PostAdminV1StoresStoreIdSalesIdSchedulePathParameters,
  PostAdminV1StoresStoreIdSalesPathParameters
} from '../model';

import { customInstance } from '../../../transport/mutator';
import type { BodyType } from '../../../transport/mutator';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];


  /**
 * Filter by status or store product. Each row carries the leader with their private maximum and, once sold, the winner and the order
 * @summary Auctions of the store, newest first
 */
export const getAdminV1StoresStoreIdAuctions = (
    { storeId }: GetAdminV1StoresStoreIdAuctionsPathParameters,
    params?: GetAdminV1StoresStoreIdAuctionsParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdAuctions200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdAuctions200>(
      {url: `/admin/v1/stores/${storeId}/auctions`, method: 'GET',
        params
    },
      options);
    }
  /**
 * One variant of a store product is the lot, quantity 1. Prices are in the store currency (422 on another). `scheduled` (the default) withdraws the product from the cart (409 PRODUCT_IN_AUCTION there) and holds the lot's stock (409 INSUFFICIENT_STOCK when a tracked variant has none); a start time already reached opens it at once. 409 PRODUCT_IN_AUCTION when the product already has a scheduled or live auction. 422 INVALID_REFERENCE when the variant is not the store product's
 * @summary Sell a store product as an auction
 */
export const postAdminV1StoresStoreIdAuctions = (
    { storeId }: PostAdminV1StoresStoreIdAuctionsPathParameters,
    postAdminV1StoresStoreIdAuctionsBody: BodyType<PostAdminV1StoresStoreIdAuctionsBody>,
 options?: SecondParameter<typeof customInstance<AdminAuction>>,) => {
      return customInstance<AdminAuction>(
      {url: `/admin/v1/stores/${storeId}/auctions`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdAuctionsBody
    },
      options);
    }
  /**
 * With real names and emails, the terms version each accepted and any block
 * @summary Shoppers registered to bid in this store, newest first
 */
export const getAdminV1StoresStoreIdAuctionsRegistrations = (
    { storeId }: GetAdminV1StoresStoreIdAuctionsRegistrationsPathParameters,
    params?: GetAdminV1StoresStoreIdAuctionsRegistrationsParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdAuctionsRegistrations200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdAuctionsRegistrations200>(
      {url: `/admin/v1/stores/${storeId}/auctions/registrations`, method: 'GET',
        params
    },
      options);
    }
  /**
 * New bids answer 403 BIDDER_BLOCKED; bids already placed stand until removed. Audited with the reason
 * @summary Block a bidder from this store's auctions
 */
export const postAdminV1StoresStoreIdAuctionsRegistrationsIdBlock = (
    { storeId, id }: PostAdminV1StoresStoreIdAuctionsRegistrationsIdBlockPathParameters,
    postAdminV1StoresStoreIdAuctionsRegistrationsIdBlockBody: BodyType<PostAdminV1StoresStoreIdAuctionsRegistrationsIdBlockBody>,
 options?: SecondParameter<typeof customInstance<AdminAuctionRegistration>>,) => {
      return customInstance<AdminAuctionRegistration>(
      {url: `/admin/v1/stores/${storeId}/auctions/registrations/${id}/block`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdAuctionsRegistrationsIdBlockBody
    },
      options);
    }
  /**
 * @summary Let a blocked bidder bid again
 */
export const postAdminV1StoresStoreIdAuctionsRegistrationsIdUnblock = (
    { storeId, id }: PostAdminV1StoresStoreIdAuctionsRegistrationsIdUnblockPathParameters,
 options?: SecondParameter<typeof customInstance<AdminAuctionRegistration>>,) => {
      return customInstance<AdminAuctionRegistration>(
      {url: `/admin/v1/stores/${storeId}/auctions/registrations/${id}/unblock`, method: 'POST'
    },
      options);
    }
  /**
 * @summary One auction with its leader, reserve and outcome
 */
export const getAdminV1StoresStoreIdAuctionsId = (
    { storeId, id }: GetAdminV1StoresStoreIdAuctionsIdPathParameters,
 options?: SecondParameter<typeof customInstance<AdminAuction>>,) => {
      return customInstance<AdminAuction>(
      {url: `/admin/v1/stores/${storeId}/auctions/${id}`, method: 'GET'
    },
      options);
    }
  /**
 * Times, prices, increments, anti-sniping, premium, and draft ↔ scheduled. 409 AUCTION_NOT_EDITABLE once it is live, ended or canceled; 409 PRODUCT_IN_AUCTION when scheduling a product that already has an auction
 * @summary Change an auction before it starts
 */
export const patchAdminV1StoresStoreIdAuctionsId = (
    { storeId, id }: PatchAdminV1StoresStoreIdAuctionsIdPathParameters,
    patchAdminV1StoresStoreIdAuctionsIdBody: BodyType<PatchAdminV1StoresStoreIdAuctionsIdBody>,
 options?: SecondParameter<typeof customInstance<AdminAuction>>,) => {
      return customInstance<AdminAuction>(
      {url: `/admin/v1/stores/${storeId}/auctions/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: patchAdminV1StoresStoreIdAuctionsIdBody
    },
      options);
    }
  /**
 * No order is placed and the stock hold is released; bids stay in the history. 409 AUCTION_NOT_EDITABLE once ended or canceled
 * @summary Cancel an auction, with or without bids
 */
export const postAdminV1StoresStoreIdAuctionsIdCancel = (
    { storeId, id }: PostAdminV1StoresStoreIdAuctionsIdCancelPathParameters,
    postAdminV1StoresStoreIdAuctionsIdCancelBody: BodyType<PostAdminV1StoresStoreIdAuctionsIdCancelBody>,
 options?: SecondParameter<typeof customInstance<AdminAuction>>,) => {
      return customInstance<AdminAuction>(
      {url: `/admin/v1/stores/${storeId}/auctions/${id}/cancel`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdAuctionsIdCancelBody
    },
      options);
    }
  /**
 * Settles it as the timed end would: the leader wins when the reserve is met (an unpaid order is placed), otherwise no sale. 409 AUCTION_NOT_LIVE
 * @summary End a live auction now
 */
export const postAdminV1StoresStoreIdAuctionsIdEnd = (
    { storeId, id }: PostAdminV1StoresStoreIdAuctionsIdEndPathParameters,
 options?: SecondParameter<typeof customInstance<AdminAuction>>,) => {
      return customInstance<AdminAuction>(
      {url: `/admin/v1/stores/${storeId}/auctions/${id}/end`, method: 'POST'
    },
      options);
    }
  /**
 * Automatic proxy bids and private raises included; removed bids show who removed them and why (includeRetracted=false hides them)
 * @summary The full bid history, newest first, with real names and private maximums
 */
export const getAdminV1StoresStoreIdAuctionsIdBids = (
    { storeId, id }: GetAdminV1StoresStoreIdAuctionsIdBidsPathParameters,
    params?: GetAdminV1StoresStoreIdAuctionsIdBidsParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdAuctionsIdBids200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdAuctionsIdBids200>(
      {url: `/admin/v1/stores/${storeId}/auctions/${id}/bids`, method: 'GET',
        params
    },
      options);
    }
  /**
 * The automatic bids placed after it go too, then the leader and price are worked out again from what stands. Reason required and audited. 409 for an automatic bid, an already removed one, or an auction that is not live
 * @summary Remove a shopper's bid from a live auction
 */
export const postAdminV1StoresStoreIdAuctionsIdBidsBidIdRetract = (
    { storeId, id, bidId }: PostAdminV1StoresStoreIdAuctionsIdBidsBidIdRetractPathParameters,
    postAdminV1StoresStoreIdAuctionsIdBidsBidIdRetractBody: BodyType<PostAdminV1StoresStoreIdAuctionsIdBidsBidIdRetractBody>,
 options?: SecondParameter<typeof customInstance<AdminAuction>>,) => {
      return customInstance<AdminAuction>(
      {url: `/admin/v1/stores/${storeId}/auctions/${id}/bids/${bidId}/retract`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdAuctionsIdBidsBidIdRetractBody
    },
      options);
    }
  /**
 * On an ended auction: the winner's unpaid order is canceled (its stock goes back), then the next-highest distinct bidder who was not offered the lot before gets an email with a signed link, at their highest bid or maximum and never below the reserve. The lot's stock is held until the offer is answered or expires (48 hours by default). After a no-sale it offers the lot to the top bidder at the reserve. Call again after a decline or expiry for the next bidder. 409 AUCTION_SECOND_CHANCE_UNAVAILABLE with meta.reason: not_ended, offer_pending, winner_paid or no_bidders_left; 409 INSUFFICIENT_STOCK when the lot sold elsewhere
 * @summary Offer the lot to the next bidder after the winner did not pay
 */
export const postAdminV1StoresStoreIdAuctionsIdSecondChance = (
    { storeId, id }: PostAdminV1StoresStoreIdAuctionsIdSecondChancePathParameters,
    postAdminV1StoresStoreIdAuctionsIdSecondChanceBody: BodyType<PostAdminV1StoresStoreIdAuctionsIdSecondChanceBody>,
 options?: SecondParameter<typeof customInstance<AdminAuction>>,) => {
      return customInstance<AdminAuction>(
      {url: `/admin/v1/stores/${storeId}/auctions/${id}/second-chance`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdAuctionsIdSecondChanceBody
    },
      options);
    }
  /**
 * @summary Second-chance offers of an auction, newest first
 */
export const getAdminV1StoresStoreIdAuctionsIdOffers = (
    { storeId, id }: GetAdminV1StoresStoreIdAuctionsIdOffersPathParameters,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdAuctionsIdOffers200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdAuctionsIdOffers200>(
      {url: `/admin/v1/stores/${storeId}/auctions/${id}/offers`, method: 'GET'
    },
      options);
    }
  /**
 * The link stops working and the stock hold is released. 409 AUCTION_OFFER_UNAVAILABLE when it is no longer pending
 * @summary Withdraw a pending second-chance offer
 */
export const postAdminV1StoresStoreIdAuctionsIdOffersOfferIdWithdraw = (
    { storeId, id, offerId }: PostAdminV1StoresStoreIdAuctionsIdOffersOfferIdWithdrawPathParameters,
 options?: SecondParameter<typeof customInstance<AdminAuction>>,) => {
      return customInstance<AdminAuction>(
      {url: `/admin/v1/stores/${storeId}/auctions/${id}/offers/${offerId}/withdraw`, method: 'POST'
    },
      options);
    }
  /**
 * A sale groups lots under one title and schedule (D-179). Filter by status. The lots of a sale come from GET /stores/{storeId}/auctions?saleId=
 * @summary Sales of the store, newest first
 */
export const getAdminV1StoresStoreIdSales = (
    { storeId }: GetAdminV1StoresStoreIdSalesPathParameters,
    params?: GetAdminV1StoresStoreIdSalesParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdSales200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdSales200>(
      {url: `/admin/v1/stores/${storeId}/sales`, method: 'GET',
        params
    },
      options);
    }
  /**
 * Title, cover, essay, viewing note and the schedule: bidding opens at startsAt on every lot, lot 1 closes at firstCloseAt and each next lot lotIntervalSeconds later. Lots join with saleId on POST /auctions or POST /sales/{id}/lots; POST /sales/{id}/schedule schedules them all. 409 ALREADY_EXISTS on a taken handle; 422 on a first close less than a minute after the start or from now
 * @summary Create a draft sale
 */
export const postAdminV1StoresStoreIdSales = (
    { storeId }: PostAdminV1StoresStoreIdSalesPathParameters,
    postAdminV1StoresStoreIdSalesBody: BodyType<PostAdminV1StoresStoreIdSalesBody>,
 options?: SecondParameter<typeof customInstance<AdminAuctionSale>>,) => {
      return customInstance<AdminAuctionSale>(
      {url: `/admin/v1/stores/${storeId}/sales`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdSalesBody
    },
      options);
    }
  /**
 * @summary One sale with its counts and totals
 */
export const getAdminV1StoresStoreIdSalesId = (
    { storeId, id }: GetAdminV1StoresStoreIdSalesIdPathParameters,
 options?: SecondParameter<typeof customInstance<AdminAuctionSale>>,) => {
      return customInstance<AdminAuctionSale>(
      {url: `/admin/v1/stores/${storeId}/sales/${id}`, method: 'GET'
    },
      options);
    }
  /**
 * Title, subtitle, cover, essay, viewing and seo in every status. Handle, times, interval and premium only while draft (409 SALE_NOT_EDITABLE); a handle change records a redirect; a time change moves the draft lots with it
 * @summary Change a sale
 */
export const patchAdminV1StoresStoreIdSalesId = (
    { storeId, id }: PatchAdminV1StoresStoreIdSalesIdPathParameters,
    patchAdminV1StoresStoreIdSalesIdBody: BodyType<PatchAdminV1StoresStoreIdSalesIdBody>,
 options?: SecondParameter<typeof customInstance<AdminAuctionSale>>,) => {
      return customInstance<AdminAuctionSale>(
      {url: `/admin/v1/stores/${storeId}/sales/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: patchAdminV1StoresStoreIdSalesIdBody
    },
      options);
    }
  /**
 * Each lot gets its close from its lot number, holds its stock and leaves the cart (409 PRODUCT_IN_AUCTION or INSUFFICIENT_STOCK name the lot that failed and nothing is scheduled). A start already reached opens the sale at once. 422 SALE_EMPTY without lots; 409 SALE_NOT_EDITABLE unless draft
 * @summary Schedule the sale and every lot in it
 */
export const postAdminV1StoresStoreIdSalesIdSchedule = (
    { storeId, id }: PostAdminV1StoresStoreIdSalesIdSchedulePathParameters,
 options?: SecondParameter<typeof customInstance<AdminAuctionSale>>,) => {
      return customInstance<AdminAuctionSale>(
      {url: `/admin/v1/stores/${storeId}/sales/${id}/schedule`, method: 'POST'
    },
      options);
    }
  /**
 * Its scheduled and live lots are cancelled (holds released, no orders); lots already sold stay sold. 409 SALE_NOT_EDITABLE once ended or cancelled
 * @summary Cancel a sale
 */
export const postAdminV1StoresStoreIdSalesIdCancel = (
    { storeId, id }: PostAdminV1StoresStoreIdSalesIdCancelPathParameters,
    postAdminV1StoresStoreIdSalesIdCancelBody: BodyType<PostAdminV1StoresStoreIdSalesIdCancelBody>,
 options?: SecondParameter<typeof customInstance<AdminAuctionSale>>,) => {
      return customInstance<AdminAuctionSale>(
      {url: `/admin/v1/stores/${storeId}/sales/${id}/cancel`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdSalesIdCancelBody
    },
      options);
    }
  /**
 * 409 LOT_IN_SALE when it is in a sale already; 409 AUCTION_NOT_EDITABLE unless the auction is draft; 409 SALE_NOT_EDITABLE unless the sale is draft
 * @summary Add a draft auction to a draft sale as its next lot
 */
export const postAdminV1StoresStoreIdSalesIdLots = (
    { storeId, id }: PostAdminV1StoresStoreIdSalesIdLotsPathParameters,
    postAdminV1StoresStoreIdSalesIdLotsBody: BodyType<PostAdminV1StoresStoreIdSalesIdLotsBody>,
 options?: SecondParameter<typeof customInstance<AdminAuctionSale>>,) => {
      return customInstance<AdminAuctionSale>(
      {url: `/admin/v1/stores/${storeId}/sales/${id}/lots`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdSalesIdLotsBody
    },
      options);
    }
  /**
 * auctionIds lists every lot once in the new order; lot numbers restart at 1 (422 otherwise). 409 SALE_NOT_EDITABLE unless draft
 * @summary Renumber the lots of a draft sale
 */
export const patchAdminV1StoresStoreIdSalesIdLotsOrder = (
    { storeId, id }: PatchAdminV1StoresStoreIdSalesIdLotsOrderPathParameters,
    patchAdminV1StoresStoreIdSalesIdLotsOrderBody: BodyType<PatchAdminV1StoresStoreIdSalesIdLotsOrderBody>,
 options?: SecondParameter<typeof customInstance<AdminAuctionSale>>,) => {
      return customInstance<AdminAuctionSale>(
      {url: `/admin/v1/stores/${storeId}/sales/${id}/lots/order`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: patchAdminV1StoresStoreIdSalesIdLotsOrderBody
    },
      options);
    }
  /**
 * The auction stays, as a standalone draft; the remaining lots are renumbered. 409 SALE_NOT_EDITABLE unless draft
 * @summary Take a lot out of a draft sale
 */
export const deleteAdminV1StoresStoreIdSalesIdLotsAuctionId = (
    { storeId, id, auctionId }: DeleteAdminV1StoresStoreIdSalesIdLotsAuctionIdPathParameters,
 options?: SecondParameter<typeof customInstance<AdminAuctionSale>>,) => {
      return customInstance<AdminAuctionSale>(
      {url: `/admin/v1/stores/${storeId}/sales/${id}/lots/${auctionId}`, method: 'DELETE'
    },
      options);
    }
  /**
 * The pending queue by default; filter by status or lot. Each row carries the asker's name, email and public bidder label (D-180)
 * @summary Questions shoppers asked on lots, newest first
 */
export const getAdminV1StoresStoreIdAuctionsQuestions = (
    { storeId }: GetAdminV1StoresStoreIdAuctionsQuestionsPathParameters,
    params?: GetAdminV1StoresStoreIdAuctionsQuestionsParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdAuctionsQuestions200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdAuctionsQuestions200>(
      {url: `/admin/v1/stores/${storeId}/auctions/questions`, method: 'GET',
        params
    },
      options);
    }
  /**
 * The question and answer appear on the lot for every shopper and the asker is emailed and pushed. 409 AUCTION_QUESTION_ANSWERED when it was answered or hidden already
 * @summary Answer a question in public
 */
export const postAdminV1StoresStoreIdAuctionsQuestionsIdAnswer = (
    { storeId, id }: PostAdminV1StoresStoreIdAuctionsQuestionsIdAnswerPathParameters,
    postAdminV1StoresStoreIdAuctionsQuestionsIdAnswerBody: BodyType<PostAdminV1StoresStoreIdAuctionsQuestionsIdAnswerBody>,
 options?: SecondParameter<typeof customInstance<AdminAuctionQuestion>>,) => {
      return customInstance<AdminAuctionQuestion>(
      {url: `/admin/v1/stores/${storeId}/auctions/questions/${id}/answer`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdAuctionsQuestionsIdAnswerBody
    },
      options);
    }
  /**
 * Never shown on the lot; the asker sees it as unanswered. Reason required and audited. An answered question can be hidden later
 * @summary Hide a question
 */
export const postAdminV1StoresStoreIdAuctionsQuestionsIdHide = (
    { storeId, id }: PostAdminV1StoresStoreIdAuctionsQuestionsIdHidePathParameters,
    postAdminV1StoresStoreIdAuctionsQuestionsIdHideBody: BodyType<PostAdminV1StoresStoreIdAuctionsQuestionsIdHideBody>,
 options?: SecondParameter<typeof customInstance<AdminAuctionQuestion>>,) => {
      return customInstance<AdminAuctionQuestion>(
      {url: `/admin/v1/stores/${storeId}/auctions/questions/${id}/hide`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdAuctionsQuestionsIdHideBody
    },
      options);
    }
  export type GetAdminV1StoresStoreIdAuctionsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdAuctions>>>
export type PostAdminV1StoresStoreIdAuctionsResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdAuctions>>>
export type GetAdminV1StoresStoreIdAuctionsRegistrationsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdAuctionsRegistrations>>>
export type PostAdminV1StoresStoreIdAuctionsRegistrationsIdBlockResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdAuctionsRegistrationsIdBlock>>>
export type PostAdminV1StoresStoreIdAuctionsRegistrationsIdUnblockResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdAuctionsRegistrationsIdUnblock>>>
export type GetAdminV1StoresStoreIdAuctionsIdResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdAuctionsId>>>
export type PatchAdminV1StoresStoreIdAuctionsIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1StoresStoreIdAuctionsId>>>
export type PostAdminV1StoresStoreIdAuctionsIdCancelResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdAuctionsIdCancel>>>
export type PostAdminV1StoresStoreIdAuctionsIdEndResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdAuctionsIdEnd>>>
export type GetAdminV1StoresStoreIdAuctionsIdBidsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdAuctionsIdBids>>>
export type PostAdminV1StoresStoreIdAuctionsIdBidsBidIdRetractResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdAuctionsIdBidsBidIdRetract>>>
export type PostAdminV1StoresStoreIdAuctionsIdSecondChanceResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdAuctionsIdSecondChance>>>
export type GetAdminV1StoresStoreIdAuctionsIdOffersResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdAuctionsIdOffers>>>
export type PostAdminV1StoresStoreIdAuctionsIdOffersOfferIdWithdrawResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdAuctionsIdOffersOfferIdWithdraw>>>
export type GetAdminV1StoresStoreIdSalesResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdSales>>>
export type PostAdminV1StoresStoreIdSalesResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdSales>>>
export type GetAdminV1StoresStoreIdSalesIdResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdSalesId>>>
export type PatchAdminV1StoresStoreIdSalesIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1StoresStoreIdSalesId>>>
export type PostAdminV1StoresStoreIdSalesIdScheduleResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdSalesIdSchedule>>>
export type PostAdminV1StoresStoreIdSalesIdCancelResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdSalesIdCancel>>>
export type PostAdminV1StoresStoreIdSalesIdLotsResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdSalesIdLots>>>
export type PatchAdminV1StoresStoreIdSalesIdLotsOrderResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1StoresStoreIdSalesIdLotsOrder>>>
export type DeleteAdminV1StoresStoreIdSalesIdLotsAuctionIdResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1StoresStoreIdSalesIdLotsAuctionId>>>
export type GetAdminV1StoresStoreIdAuctionsQuestionsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdAuctionsQuestions>>>
export type PostAdminV1StoresStoreIdAuctionsQuestionsIdAnswerResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdAuctionsQuestionsIdAnswer>>>
export type PostAdminV1StoresStoreIdAuctionsQuestionsIdHideResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdAuctionsQuestionsIdHide>>>
